import './InfoCardBody.scss';
import { Dropdown, DropdownItem, Icons, Tooltip } from 'plume-ui';
import { memo, PropsWithChildren, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Routes } from '../../../../../config';
// Hooks
import { useTranslation } from 'react-i18next';
import {
  AdjustableComponent,
  useClasses,
} from '../../../../../utils/hooks/useClasses';
import { useCreateRoute } from '../../../../../utils/hooks/useRedirectToRoute';
import { getTimeRanges, TimeRange } from '../helpers';

export type InfoCardBodyProps = PropsWithChildren<{
  label: string;
  labelTooltip?: string;
  optionalTitle?: string;
  showEdit?: boolean;
  showMenu?: boolean;
  editRoute?: { route: Routes; params?: { [key: string]: string | undefined } };
  onMenuItemSelect?: (item: TimeRange) => void;
  options?: TimeRange[];
  defaultSelected?: number;
}>;

type InfoCardBodyStyles = {
  infoCard: string;
  title: string;
  titleRow: string;
  optionalTitle: string;
  edit: string;
  menu: string;
};

const InfoCardBody: AdjustableComponent<
  InfoCardBodyProps,
  InfoCardBodyStyles
> = ({
  classes,
  children,
  label,
  showEdit,
  showMenu,
  labelTooltip,
  optionalTitle,
  editRoute,
  onMenuItemSelect,
  options,
  defaultSelected,
}) => {
  const { t } = useTranslation();
  const routeToRedirect = useCreateRoute();
  const styles: InfoCardBodyStyles = useClasses(
    {
      infoCard: `MonitorInfo__info-card`,
      title: 'MonitorInfo__info-card__title',
      titleRow: 'MonitorInfo__info-card__titleRow',
      optionalTitle: 'MonitorInfo__info-card__title--optional',
      edit: 'MonitorInfo__info-card__action-edit',
      menu: 'MonitorInfo__info-card__action-menu',
    },
    classes,
  );

  const timeRanges = useMemo(() => options ?? getTimeRanges(), [options]);

  const [
    selectedItemObject,
    setSelectedItemObject,
  ] = useState<TimeRange | null>(timeRanges[defaultSelected ?? 0]);

  const selectOption = (index: number): (() => void) => (): void => {
    if (options === undefined) return;
    setSelectedItemObject(timeRanges[index]);
    options[index].selected = !options[index].selected;
    if (onMenuItemSelect) {
      onMenuItemSelect(timeRanges[index]);
    }
  };

  return (
    <div className={styles.infoCard}>
      <div className={styles.titleRow}>
        <div className={styles.title}>{label}</div>
        {optionalTitle && (
          <span className={styles.optionalTitle}>{optionalTitle}</span>
        )}
        {labelTooltip && (
          <Tooltip
            label={labelTooltip}
            overrideMaxCharacters
            liveMeasure={false}
            openInPortal
          >
            <Icons.InfoIcon width={16} />
          </Tooltip>
        )}
      </div>
      {showEdit && (
        <div className={styles.edit}>
          <Link
            to={{
              pathname: routeToRedirect(
                editRoute ? editRoute.route : Routes.HealthSummary,
                editRoute?.params,
              ),
              state: editRoute?.params,
            }}
          >
            {t('actions.edit')}
          </Link>
        </div>
      )}
      {showMenu && (
        <div className={styles.menu}>
          <Dropdown
            label={selectedItemObject?.label}
            iconLeft={<Icons.CalendarIcon width={24} />}
            closeOnItemClick={true}
            listPosition="right"
            openInPortal
          >
            {timeRanges.map((option: TimeRange, index: number) => (
              <DropdownItem
                key={index}
                onClick={selectOption(index)}
                selected={selectedItemObject?.label === option.label}
              >
                {option.label}
              </DropdownItem>
            ))}
          </Dropdown>
        </div>
      )}
      {children}
    </div>
  );
};

export default memo(InfoCardBody);
